import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, Navigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Circle, Truck, Package, Home, XCircle, Download, FileText, Calendar, Radio } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { toast } from 'sonner';
import { useShopAuth } from '../../context/ShopAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

const TIMELINE = [
  { key: 'pending_payment', label: 'Order placed', icon: Circle },
  { key: 'confirmed', label: 'Payment confirmed', icon: CheckCircle2 },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
  { key: 'en_route', label: 'En route', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Home },
  { key: 'received', label: 'Received', icon: CheckCircle2 },
];

const statusIndex = (s) => TIMELINE.findIndex(t => t.key === s);

export default function ShopOrderDetail() {
  const { id } = useParams();
  const { customer, loading, authHeader } = useShopAuth();
  const [order, setOrder] = useState(null);
  const [riderLoc, setRiderLoc] = useState(null);

  const load = () => {
    axios.get(`${API}/shop/orders/mine/${id}`, { headers: authHeader() }).then(r => setOrder(r.data)).catch(() => setOrder(false));
  };
  const loadRiderLoc = () => {
    axios.get(`${API}/shop/orders/mine/${id}/rider-location`, { headers: authHeader() })
      .then(r => setRiderLoc(r.data?.available ? r.data : null))
      .catch(() => setRiderLoc(null));
  };
  useEffect(() => { if (customer) load(); /* eslint-disable-next-line */ }, [customer, id]);
  useEffect(() => {
    if (!customer || !order) return;
    if (!['dispatched', 'en_route', 'delivered'].includes(order.status)) { setRiderLoc(null); return; }
    loadRiderLoc();
    const t = setInterval(loadRiderLoc, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [customer, order?.status, id]);

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!customer) return <Navigate to="/shop/login" replace />;
  if (order === false) return <div className="p-8 text-muted-foreground">Order not found.</div>;
  if (!order) return <div className="p-8 text-muted-foreground">Loading order…</div>;

  const idx = statusIndex(order.status);
  const cancelled = order.status === 'cancelled';

  const confirmReceipt = async () => {
    try {
      await axios.post(`${API}/shop/orders/mine/${id}/confirm-receipt`, {}, { headers: authHeader() });
      toast.success('Thank you! Order marked as received.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const downloadEbm = async () => {
    try {
      const r = await axios.get(`${API}/shop/orders/mine/${id}/ebm-invoice`, { headers: authHeader() });
      const a = document.createElement('a');
      a.href = r.data.file;
      a.download = r.data.filename || `ebm-invoice-${order.order_number}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to download'); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-serif font-bold">Order #{order.order_number}</h1>
        <p className="text-sm text-muted-foreground">Placed {new Date(order.created_at).toLocaleString()}</p>
      </div>

      {/* Timeline */}
      <section className="p-5 bg-card rounded-xl border border-border" data-testid="order-timeline">
        {cancelled ? (
          <div className="flex items-center gap-3 text-red-600"><XCircle className="w-6 h-6" /><div className="font-semibold">Order cancelled</div></div>
        ) : (
          <ol className="space-y-2">
            {TIMELINE.map((step, i) => {
              const reached = i <= idx;
              const Icon = step.icon;
              return (
                <li key={step.key} className={`flex items-center gap-3 ${reached ? 'text-foreground' : 'text-muted-foreground'}`} data-testid={`timeline-${step.key}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${reached ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 font-medium">{step.label}</div>
                </li>
              );
            })}
          </ol>
        )}
        {order.status === 'delivered' && (
          <Button className="mt-4 w-full" onClick={confirmReceipt} data-testid="confirm-receipt-btn">Confirm receipt</Button>
        )}
      </section>

      {/* Items */}
      <section className="p-5 bg-card rounded-xl border border-border">
        <h2 className="font-semibold mb-3">Items</h2>
        <div className="space-y-2">
          {order.items.map(it => (
            <div key={it.product_id} className="flex justify-between text-sm">
              <span>{it.product_name} <span className="text-muted-foreground">× {it.quantity}</span></span>
              <span>RWF {formatRWF(it.line_total)}</span>
            </div>
          ))}
          <div className="border-t border-border pt-2 mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>RWF {formatRWF(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-green-700"><span>Promo {order.promo_code}</span><span>-RWF {formatRWF(order.discount)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span>{order.delivery_fee ? `RWF ${formatRWF(order.delivery_fee)}` : 'Free'}</span></div>
            <div className="flex justify-between font-bold pt-2 border-t border-border mt-2"><span>Total</span><span>RWF {formatRWF(order.total)}</span></div>
          </div>
        </div>
      </section>

      {/* Live rider tracking */}
      {riderLoc && riderLoc.location && (
        <section className="p-5 bg-card rounded-xl border border-border" data-testid="rider-tracking-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h2 className="font-semibold flex items-center gap-2"><Radio className="w-4 h-4 text-green-600 animate-pulse" />Live rider location</h2>
            {riderLoc.eta_minutes !== null && riderLoc.eta_minutes !== undefined && (
              <div className="text-sm px-3 py-1 rounded-full bg-green-50 text-green-800 border border-green-200" data-testid="rider-eta">
                <strong>Arriving in ~{riderLoc.eta_minutes} min</strong>
                {riderLoc.distance_km !== null && <span className="text-xs text-green-700 ml-2">· {riderLoc.distance_km} km away</span>}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">Updated {new Date(riderLoc.location.updated_at).toLocaleTimeString()} · refreshes every 15s</p>
          <div className="rounded-lg overflow-hidden border border-border" style={{ height: 280 }}>
            {GMAP_KEY ? (
              <APIProvider apiKey={GMAP_KEY}>
                <Map
                  defaultCenter={{ lat: riderLoc.location.lat, lng: riderLoc.location.lng }}
                  defaultZoom={14}
                  mapId="bison-tracking-map"
                  gestureHandling="greedy"
                  disableDefaultUI={false}
                >
                  <AdvancedMarker position={{ lat: riderLoc.location.lat, lng: riderLoc.location.lng }} title="Rider">
                    <div className="w-8 h-8 rounded-full bg-green-600 border-2 border-green-800 flex items-center justify-center shadow-lg">
                      <Radio className="w-4 h-4 text-white" />
                    </div>
                  </AdvancedMarker>
                  {riderLoc.destination?.lat && (
                    <AdvancedMarker position={{ lat: riderLoc.destination.lat, lng: riderLoc.destination.lng }} title="You">
                      <div className="w-7 h-7 rounded-full bg-primary border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold">You</div>
                    </AdvancedMarker>
                  )}
                </Map>
              </APIProvider>
            ) : <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Map unavailable.</div>}
          </div>
        </section>
      )}

      {/* Delivery */}
      <section className="p-5 bg-card rounded-xl border border-border">
        <h2 className="font-semibold mb-2">Delivery</h2>
        <p className="text-sm text-muted-foreground">{order.address?.label}: {order.address?.line}, {order.address?.city}</p>
        {order.address?.notes && <p className="text-xs text-muted-foreground mt-1">Notes: {order.address.notes}</p>}
        {order.scheduled_for && (
          <p className="text-sm mt-2 flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" /><span className="text-muted-foreground">Scheduled for:</span> {new Date(order.scheduled_for).toLocaleString()}</p>
        )}
        {order.delivery_zone && (
          <p className="text-xs text-muted-foreground mt-1">Zone: {order.delivery_zone}{order.delivery_distance_km ? ` · ${order.delivery_distance_km} km` : ''}</p>
        )}
        <p className="text-sm mt-2"><span className="text-muted-foreground">Payment:</span> {order.payment_method.replace(/_/g, ' ')}</p>
        {order.customer_notes && (
          <div className="mt-3 p-3 rounded bg-muted/40 border border-border text-sm">
            <div className="text-xs text-muted-foreground mb-0.5">Your note</div>
            <div>{order.customer_notes}</div>
          </div>
        )}
      </section>

      {/* EBM Invoice */}
      {(order.ebm_invoice_request || order.ebm_invoice_filename) && (
        <section className="p-5 bg-card rounded-xl border border-border" data-testid="ebm-section">
          <h2 className="font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-primary" />EBM Invoice</h2>
          {order.ebm_invoice_request && (
            <div className="text-xs text-muted-foreground space-y-0.5 mb-3">
              <div><span className="font-medium text-foreground">Business:</span> {order.ebm_invoice_request.business_name}</div>
              {order.ebm_invoice_request.tin && <div><span className="font-medium text-foreground">TIN:</span> {order.ebm_invoice_request.tin}</div>}
              {order.ebm_invoice_request.purchase_code && <div><span className="font-medium text-foreground">Purchase code:</span> {order.ebm_invoice_request.purchase_code}</div>}
            </div>
          )}
          {order.ebm_invoice_filename ? (
            <Button onClick={downloadEbm} variant="outline" data-testid="ebm-download-btn"><Download className="w-4 h-4 mr-1" />Download {order.ebm_invoice_filename}</Button>
          ) : (
            <p className="text-sm text-muted-foreground">We'll upload the EBM invoice once your order is processed. You'll get a notification.</p>
          )}
        </section>
      )}
    </div>
  );
}
